import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

export type UiIconName =
  | 'loader'
  | 'check'
  | 'x'
  | 'chevron-down'
  | 'chevron-left'
  | 'search'
  | 'eye'
  | 'eye-off'
  | 'alert-circle'
  | 'info'
  | 'alert-triangle'
  | 'edit'
  | 'trash'
  | 'plus'
  | 'minus'
  | 'contacts'
  | 'building'
  | 'lock'
  | 'user'
  | 'group'
  | 'pin'
  | 'upload'
  | 'folder-add'
  | 'sidebar'
  | 'rotate-left'
  | 'rotate-right'
  | 'flip-h'
  | 'flip-v'
  | 'undo'
  | 'redo'
  | 'refresh'
  | 'crop'
  | 'image';

/** Remix Icon class names (see https://remixicon.com). */
const REMIX_CLASS: Record<UiIconName, string> = {
  loader: 'ri-loader-4-line',
  check: 'ri-check-line',
  x: 'ri-close-line',
  'chevron-down': 'ri-arrow-down-s-line',
  'chevron-left': 'ri-arrow-left-s-line',
  search: 'ri-search-line',
  eye: 'ri-eye-line',
  'eye-off': 'ri-eye-off-line',
  'alert-circle': 'ri-error-warning-line',
  info: 'ri-information-line',
  'alert-triangle': 'ri-alert-line',
  edit: 'ri-pencil-line',
  trash: 'ri-delete-bin-6-line',
  plus: 'ri-add-line',
  minus: 'ri-subtract-line',
  contacts: 'ri-contacts-book-line',
  building: 'ri-building-line',
  lock: 'ri-lock-line',
  user: 'ri-user-line',
  group: 'ri-group-line',
  pin: 'ri-pushpin-2-fill',
  upload: 'ri-upload-2-line',
  'folder-add': 'ri-folder-add-line',
  sidebar: 'ri-side-bar-line',
  'rotate-left': 'ri-anticlockwise-2-line',
  'rotate-right': 'ri-clockwise-2-line',
  'flip-h': 'ri-arrow-left-right-line',
  'flip-v': 'ri-arrow-up-down-line',
  undo: 'ri-arrow-go-back-line',
  redo: 'ri-arrow-go-forward-line',
  refresh: 'ri-refresh-line',
  crop: 'ri-crop-line',
  image: 'ri-image-line',
};

@Component({
  selector: 'app-icon',
  standalone: true,
  template: `
    <i
      [class]="iconClass()"
      [style.font-size.px]="size()"
      [attr.aria-hidden]="decorative()"
      [attr.role]="decorative() ? null : 'img'"
      [attr.aria-label]="decorative() ? null : label()"
    ></i>
  `,
  styles: `
    :host {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      line-height: 0;
      color: var(--ui-icon-color, currentColor);
    }
    .ui-icon {
      line-height: 1;
    }
    .ui-icon--spin {
      animation: ui-spin 0.8s linear infinite;
    }
    @keyframes ui-spin {
      to {
        transform: rotate(360deg);
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IconComponent {
  readonly name = input.required<UiIconName>();
  readonly size = input(16);
  readonly label = input('');
  readonly decorative = input(true);

  readonly iconClass = computed(() => {
    const n = this.name();
    const glyph = REMIX_CLASS[n] ?? REMIX_CLASS['info'];
    const spin = n === 'loader' ? ' ui-icon--spin' : '';
    return `ui-icon ri ${glyph}${spin}`;
  });
}
